import type { DefaultTheme } from "styled-components";
import { AG3Theme } from "../theme";
import { mix, parseToRgb, rgba } from "polished";
import { AllTokenKeys, allTokens, getTokenValue } from "../designTokens";
import type {
  ThemeObjectSchema,
  DesignTokensSchema,
  Branding,
} from "./themeTypes";

/**
 * Creates a new theme instance with custom branding and design tokens
 * @param brandingOrThemeObject: Either legacy Branding object or new ThemeObjectSchema
 * @param theme_name: a name peculiar to a storefront, could be the slug or tenant_id
 * @returns DefaultTheme
 */
export function makeNewTheme(
  brandingOrThemeObject: Partial<Branding> | Partial<ThemeObjectSchema>,
  theme_name: string
): DefaultTheme {
  const newTheme = JSON.parse(JSON.stringify(AG3Theme)) as DefaultTheme;

  // Determine if this is the new schema or legacy branding
  const isNewSchema =
    "version" in brandingOrThemeObject ||
    "designTokens" in brandingOrThemeObject;

  let branding: Partial<Branding>;
  let designTokens: DesignTokensSchema | undefined;

  if (isNewSchema) {
    const themeObj = brandingOrThemeObject as Partial<ThemeObjectSchema>;
    branding = themeObj.branding || {};
    designTokens = themeObj.designTokens;
  } else {
    // Check if it's the legacy format with nested branding
    if (
      "branding" in brandingOrThemeObject &&
      !("version" in brandingOrThemeObject)
    ) {
      branding = (brandingOrThemeObject as any).branding || {};
    } else {
      branding = brandingOrThemeObject as Partial<Branding>;
    }
  }

  // Apply legacy branding variables
  if (branding.brandColor) {
    newTheme.brandColor = branding.brandColor;
    newTheme.primaryButtonBG = mix(0.03, branding.brandColor, "#fff");
    newTheme.primaryButtonBorder = branding.brandColor;
    newTheme.tertiaryTextColor = branding.brandColor;
    newTheme.publicNavBorderActive = branding.brandColor;
    newTheme.publicLogoBGColor = branding.brandColor;
    newTheme.primaryLinkHover = branding.brandColor;
    newTheme.secondaryLinkHover = branding.brandColor;
    newTheme.tertiaryLinkHover = branding.brandColor;
    newTheme.tertiaryButtonSelected = mix(0.03, branding.brandColor, "#fff");
    newTheme.navIconActive = branding.brandColor;
    newTheme.selectedBorder = branding.brandColor;
    newTheme.primaryButtonHover = mix(0.08, branding.brandColor, "#fff");
    newTheme.primaryButtonActive = mix(0.11, branding.brandColor, "#fff");

    // Update primitiveTokens.colors for the new token system
    if (newTheme.primitiveTokens && newTheme.primitiveTokens.colors) {
      newTheme.primitiveTokens.colors.color_brand_primary = branding.brandColor;
      try {
        const baseColorRGB = parseToRgb(branding.brandColor);
        newTheme.primitiveTokens.colors.color_brand_3 = rgba(
          baseColorRGB.red,
          baseColorRGB.green,
          baseColorRGB.blue,
          0.03
        );
        newTheme.primitiveTokens.colors.color_brand_5 = rgba(
          baseColorRGB.red,
          baseColorRGB.green,
          baseColorRGB.blue,
          0.05
        );
        newTheme.primitiveTokens.colors.color_brand_10 = rgba(
          baseColorRGB.red,
          baseColorRGB.green,
          baseColorRGB.blue,
          0.1
        );
        newTheme.primitiveTokens.colors.color_brand_15 = rgba(
          baseColorRGB.red,
          baseColorRGB.green,
          baseColorRGB.blue,
          0.15
        );
        // Add mixed color for nav background (3% brand with 97% white)
        newTheme.primitiveTokens.colors.color_brand_mixed = mix(
          0.03,
          branding.brandColor,
          "#fff"
        );
      } catch (error) {
        console.error(
          "Failed to parse branding.brandColor for token generation:",
          branding.brandColor,
          error
        );
      }
    }
  }

  if (branding.portalLogoUrl) {
    newTheme.portalLogoUrl = branding.portalLogoUrl;
  }
  if (branding.publicLogoUrl) {
    newTheme.publicLogoUrl = branding.publicLogoUrl;
  }
  if (branding.customFontFamily) {
    // Note: customFontFamily currently doesn't override theme.fontFamily or tokenized font families.
    // This behavior is preserved. AG3Theme.fontFamily is hardcoded via themeDefaults.
    // To make customFontFamily effective, AG3Theme setup or this function would need to use it.
  }
  if (branding.favIconUrl) {
    // favIconUrl is stored on the theme but not directly used by styled-components.
    // This behavior is preserved.
  }

  // Apply new design tokens if provided
  if (designTokens) {
    applyDesignTokensToTheme(newTheme, designTokens);
  }

  // Construct the complete set of tokens for this specific theme instance
  const themeSpecificPrimitives = {
    ...(newTheme.primitiveTokens.fontFamilies || {}),
    ...(newTheme.primitiveTokens.fontWeights || {}),
    ...(newTheme.primitiveTokens.fontSizes || {}),
    ...(newTheme.primitiveTokens.lineHeights || {}),
    ...(newTheme.primitiveTokens.colors || {}), // Includes updated brand colors
    ...(newTheme.primitiveTokens.spacing || {}),
    ...(newTheme.primitiveTokens.radii || {}),
    ...(newTheme.primitiveTokens.borders || {}),
    ...(newTheme.primitiveTokens.widths || {}),
  };

  const allTokensForThisTheme = {
    ...allTokens, // Base global tokens from designTokens.ts
    ...themeSpecificPrimitives, // Theme-specific primitive values override the base
  };

  // == Set up new top-level resolvers and token map for this theme instance ==
  newTheme.allTokensForTheme = allTokensForThisTheme;
  newTheme.resolve = createEnhancedResolver(
    newTheme,
    designTokens,
    allTokensForThisTheme
  );
  newTheme.resolveSizeToNumber = (
    tokenNameOrValue: AllTokenKeys | undefined
  ) => {
    if (!tokenNameOrValue) return 0;
    const resolvedValue = newTheme.resolve(tokenNameOrValue);
    const num = parseFloat(resolvedValue);
    return isNaN(num) ? 0 : num;
  };

  // Ensure top-level categories in the deprecated `tokens` object also point to the theme's categories
  // This is if `applyTokenNameOverrides` modified the top-level ones.
  if (newTheme.tokens) {
    newTheme.tokens.buttons = newTheme.buttons;
    newTheme.tokens.semanticColors = newTheme.semanticColors;
    newTheme.tokens.primitiveTokens = newTheme.primitiveTokens;
    newTheme.tokens.disabledGlobal = newTheme.disabledGlobal;
  }

  newTheme.theme_name = theme_name;
  return newTheme;
}

/**
 * Applies designTokens overrides to the theme's primitive token collections.
 * This function modifies the theme's primitiveTokens directly to include
 * custom colors and borders defined in the designTokens structure.
 *
 * @param theme - The DefaultTheme instance to modify
 * @param designTokens - The design tokens schema containing custom overrides
 */
function applyDesignTokensToTheme(
  theme: DefaultTheme,
  designTokens: DesignTokensSchema
): void {
  // Apply custom colors directly to primitive tokens
  if (designTokens.customColors) {
    Object.entries(designTokens.customColors).forEach(([key, value]) => {
      (theme.primitiveTokens.colors as Record<string, string>)[key] = value;
    });
  }

  // Apply custom borders directly to primitive tokens
  if (designTokens.customBorders) {
    Object.entries(designTokens.customBorders).forEach(([key, value]) => {
      (theme.primitiveTokens.borders as Record<string, string>)[key] = value;
    });
  }

  // CardProduct and Button tokens are handled by the enhanced resolver
  // No need to modify theme structure directly
}

/**
 * Creates an enhanced token resolver that prioritizes designTokens overrides
 * before falling back to the standard token resolution system.
 *
 * This resolver checks for token overrides in the following order:
 * 1. designTokens overrides (CardProduct, Button tokens with custom/token values)
 * 2. Custom colors and borders (direct string values)
 * 3. Standard token resolution from allTokensForTheme
 *
 * @param _theme - The DefaultTheme instance (for potential future use)
 * @param designTokens - Optional design tokens schema with overrides
 * @param allTokensForTheme - Complete token map for this theme instance
 * @returns A resolver function that accepts token names and returns resolved values
 */
function createEnhancedResolver(
  _theme: DefaultTheme,
  designTokens: DesignTokensSchema | undefined,
  allTokensForTheme: Record<string, string>
) {
  return (tokenNameOrValue: string | undefined): string => {
    if (!tokenNameOrValue) return "";

    // Check for designTokens overrides first
    if (designTokens) {
      // Check custom colors first
      if (
        designTokens.customColors &&
        designTokens.customColors[tokenNameOrValue]
      ) {
        return designTokens.customColors[tokenNameOrValue];
      }

      // Check custom borders
      if (
        designTokens.customBorders &&
        designTokens.customBorders[tokenNameOrValue]
      ) {
        return designTokens.customBorders[tokenNameOrValue];
      }

      // Check CardProduct tokens
      if (
        designTokens.cardProduct &&
        (designTokens.cardProduct as any)[tokenNameOrValue]
      ) {
        const tokenValue = (designTokens.cardProduct as any)[tokenNameOrValue];
        if (typeof tokenValue === "object") {
          if (tokenValue.type === "custom") {
            let customValue = tokenValue.value;

            // For shadow tokens, ensure numeric values have px units
            if (tokenNameOrValue && tokenNameOrValue.includes("shadow")) {
              if (
                typeof customValue === "number" ||
                (typeof customValue === "string" && /^\d+$/.test(customValue))
              ) {
                customValue = `${customValue}px`;
              }
            }

            return customValue;
          } else if (tokenValue.type === "token") {
            return getTokenValue(tokenValue.value, allTokensForTheme);
          }
        }
      }

      // Check FilterPanel tokens
      if (
        designTokens.filterPanel &&
        (designTokens.filterPanel as any)[tokenNameOrValue]
      ) {
        const tokenValue = (designTokens.filterPanel as any)[tokenNameOrValue];
        if (typeof tokenValue === "object") {
          if (tokenValue.type === "custom") {
            return tokenValue.value;
          } else if (tokenValue.type === "token") {
            return getTokenValue(tokenValue.value, allTokensForTheme);
          }
        }
      }

      // Check Button tokens
      if (
        designTokens.buttons &&
        (designTokens.buttons as any)[tokenNameOrValue]
      ) {
        const tokenValue = (designTokens.buttons as any)[tokenNameOrValue];
        if (typeof tokenValue === "object") {
          if (tokenValue.type === "custom") {
            return tokenValue.value;
          } else if (tokenValue.type === "token") {
            return getTokenValue(tokenValue.value, allTokensForTheme);
          }
        }
      }
    }

    // Fall back to normal resolution
    return getTokenValue(tokenNameOrValue, allTokensForTheme);
  };
}
